/**
 * Crossover API service for fetching team activity data
 * Handles team member worklog retrieval and Cursor time extraction
 */

import { CrossoverTeamActivityResponse, CrossoverTeamMemberActivity } from '../types';
import { AuthError } from './authService';
import { calculateCursorTime, UserTimeData } from './timeCalculationService';

// Constants
const BASE_URL = 'https://api.crossover.com';

/**
 * Crossover API error class for better error handling
 */
export class CrossoverAPIError extends Error {
  constructor(message: string, public statusCode?: number) {
    super(message);
    this.name = 'CrossoverAPIError';
  }
}

/**
 * Get Monday date of the current week in YYYY-MM-DD format
 * @param date - Optional date to get Monday for (defaults to current date)
 * @returns string - Monday date in YYYY-MM-DD format
 */
export const getCurrentWeekMonday = (date?: Date): string => {
  const currentDate = date || new Date();
  const dayOfWeek = currentDate.getDay();
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday = 0, so we need 6 days back
  
  const monday = new Date(currentDate);
  monday.setDate(currentDate.getDate() - daysToSubtract);
  
  // Format as YYYY-MM-DD
  return monday.toISOString().split('T')[0];
};

/**
 * Get Monday date for a specific week offset from current week
 * @param weeksOffset - Number of weeks to offset (negative for past weeks)
 * @returns string - Monday date in YYYY-MM-DD format
 */
export const getWeekMondayWithOffset = (weeksOffset: number): string => {
  const currentDate = new Date();
  currentDate.setDate(currentDate.getDate() + (weeksOffset * 7));
  return getCurrentWeekMonday(currentDate);
};

/**
 * Fetch team activity data from Crossover API
 * @param token - Authentication token
 * @param managerId - Manager ID from user profile
 * @param weekDate - Monday date of the week to fetch (YYYY-MM-DD format)
 * @returns Promise<CrossoverTeamActivityResponse> - Team activity data
 */
export const fetchTeamActivity = async (
  token: string,
  managerId: number,
  weekDate?: string
): Promise<CrossoverTeamActivityResponse> => {
  try {
    const mondayDate = weekDate || getCurrentWeekMonday();
    
    const url = new URL(`${BASE_URL}/api/tracker/activity/groups`);
    url.searchParams.append('date', mondayDate);
    url.searchParams.append('fullTeam', 'false');
    url.searchParams.append('groups', 'groups');
    url.searchParams.append('managerId', managerId.toString());
    url.searchParams.append('weekly', 'true');

    console.log('Fetching team activity for:', {
      managerId,
      date: mondayDate,
      url: url.toString()
    });

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'X-Auth-Token': token,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new AuthError('Session expired, please log in again', 401);
      } else if (response.status === 403) {
        throw new CrossoverAPIError('Access forbidden - insufficient permissions', 403);
      } else if (response.status === 404) {
        throw new CrossoverAPIError('Team data not found for the specified date', 404);
      } else {
        throw new CrossoverAPIError(`Failed to fetch team activity: ${response.statusText}`, response.status);
      }
    }

    const data: CrossoverTeamActivityResponse = await response.json();
    
    console.log(`Successfully fetched activity data for ${data.length} team members`);
    
    return data;
  } catch (error) {
    console.error('Fetch team activity error:', error);
    
    if (error instanceof AuthError || error instanceof CrossoverAPIError) {
      throw error;
    }
    
    throw new CrossoverAPIError('Network error or server unavailable');
  }
};

/**
 * Extract Cursor time from team member activity data
 * @param memberActivity - Single team member's activity data
 * @returns number - Time spent in Cursor in minutes (0 if not found)
 */
export const extractCursorTime = (memberActivity: CrossoverTeamMemberActivity): number => {
  try {
    const memberName = memberActivity.assignment?.candidate?.printableName || 'Unknown';
    const memberEmail = memberActivity.assignment?.candidate?.email || 'Unknown';
    
    console.log(`🔍 [CURSOR TIME EXTRACTION] Processing member: ${memberName} (${memberEmail})`);
    
    // Convert to UserTimeData format for the new calculation service
    const userData: UserTimeData = {
      grouping: {
        periodLong: memberActivity.grouping?.periodLong || 0,
        totalTrackedTime: memberActivity.grouping?.totalTrackedTime || 0,
        alignmentScore: memberActivity.grouping?.alignmentScore || 0,
        advancedGroups: memberActivity.grouping?.advancedGroups || []
      }
    };
    
    // Use the new standardized calculation service
    const cursorTime = calculateCursorTime(userData.grouping.advancedGroups);
    
    if (cursorTime === 0) {
      console.log(`❌ [CURSOR TIME] No cursor application found in any AI section for ${memberName}`);
    } else {
      console.log(`🎯 [CURSOR TIME] Total cursor time for ${memberName}: ${cursorTime} minutes`);
    }
    
    return cursorTime;
  } catch (error) {
    console.error('🚨 [CURSOR TIME] Error extracting cursor time:', error);
    return 0;
  }
};

/**
 * Extract team member information and cursor usage
 * @param teamActivity - Full team activity response
 * @returns Array of objects with member info and cursor time
 */
export const extractTeamCursorData = (teamActivity: CrossoverTeamActivityResponse) => {
  return teamActivity.map(memberActivity => ({
    memberId: memberActivity.assignment?.candidate?.id || 0,
    memberName: memberActivity.assignment?.candidate?.printableName || 'Unknown',
    memberEmail: memberActivity.assignment?.candidate?.email || '', // Personal email
    companyEmail: memberActivity.assignment?.assignmentHistory?.[0]?.candidate?.email || '', // Company email from assignment history
    cursorTimeMinutes: extractCursorTime(memberActivity),
    totalTrackedTime: memberActivity.grouping?.totalTrackedTime || 0,
    jobTitle: memberActivity.assignment?.jobTitle || '',
    status: memberActivity.assignment?.status || ''
  }));
};

/**
 * Get available weeks for dropdown selection
 * @param weeksBack - Number of past weeks to include (default: 8)
 * @returns Array of week options with labels and values
 */
export const getAvailableWeeks = (weeksBack: number = 8) => {
  const weeks = [];
  
  for (let i = 0; i < weeksBack; i++) {
    const mondayDate = getWeekMondayWithOffset(-i);
    const date = new Date(mondayDate);
    const endDate = new Date(date);
    endDate.setDate(date.getDate() + 6);
    
    const label = i === 0 
      ? `Current Week (${date.toLocaleDateString()} - ${endDate.toLocaleDateString()})`
      : `${date.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
    
    weeks.push({
      value: mondayDate,
      label,
      isCurrent: i === 0
    });
  }
  
  return weeks;
}; 